import React from 'react';
import { Typography } from '@app/ui';
import classNames from '@app/ui/sports-components/utils/classNames';

interface Identifier {
    entity: string;
    value: string;
}

interface Insight {
    insight_id: string;
    overview: string;
    identifiers: Identifier[];
}

interface InsightCardProps {
    insight: Insight;
    isActive: boolean;
    icon: string;
    onSelect: () => void;
}

const extractIdentifiers = (identifiers: Identifier[]) => {
    return identifiers.reduce<{
        rightsholder?: string;
        sponsor?: string;
        placement?: string;
    }>((acc, identifier) => {
        if (identifier.entity === 'rightsholders') {
            acc.rightsholder = identifier.value;
        }
        if (identifier.entity === 'sponsors') {
            acc.sponsor = identifier.value;
        }
        if (
            identifier.entity === 'placements' ||
            identifier.entity === 'placement_types'
        ) {
            acc.placement = identifier.value;
        }
        return acc;
    }, {});
};

const formatOverview = (overview: string) => {
    return overview
        .replace(
            /decreased/g,
            '<span style="color: #D40F7D; font-weight: 700;">decreased</span>',
        )
        .replace(
            /lower/g,
            '<span style="color: #D40F7D; font-weight: 700;">lower</span>',
        )
        .replace(
            /increased/g,
            '<span style="color: #2DC850; font-weight: 700;">increased</span>',
        )
        .replace(
            /higher/g,
            '<span style="color: #2DC850; font-weight: 700;">higher</span>',
        );
};

const InsightCard: React.FC<InsightCardProps> = ({
    insight,
    isActive,
    icon,
    onSelect,
}) => {
    const { rightsholder, sponsor, placement } = extractIdentifiers(
        insight.identifiers,
    );
    const formattedOverview = formatOverview(insight.overview);

    const cardClassName = classNames('automated-insights-cards-content-card', {
        'automated-insights-cards-content-card-active': isActive,
    });

    return (
        <div
            className={cardClassName}
            style={{
                borderRadius: 10,
                borderColor: isActive ? '#529EBD' : '#BFBFBF',
                backgroundColor: isActive ? '#FAFAFA' : '#FFFFFF',
                padding: 0,
                boxShadow: null,
            }}
            onClick={onSelect}
        >
            <div className="automated-insights-cards-content-card-header">
                <div
                    style={{
                        padding: '16px 16px 0 16px',
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <Typography.Text
                        style={{
                            fontSize: '14px',
                            fontWeight: 600,
                        }}
                    >
                        {rightsholder}
                    </Typography.Text>

                    <Typography.Text
                        style={{
                            fontSize: '14px',
                            color: '#acacac',
                        }}
                    >
                        {sponsor} {placement ? `- ${placement}` : ''}
                    </Typography.Text>
                </div>

                <div className="automated-insights-cards-content-card-header-seperator" />

                <div
                    className="automated-insights-cards-content-card-header-description"
                    style={{
                        padding: '8px 16px 16px 16px',
                        display: 'flex',
                        alignItems: 'center',
                    }}
                >
                    <div
                        className="automated-insights-cards-content-card-header-icon"
                        style={{
                            backgroundColor: '#deeffd',
                            borderRadius: '50%',
                        }}
                    >
                        <img
                            src={icon}
                            style={{
                                padding: 4,
                            }}
                        />
                    </div>

                    <div className="automated-insights-cards-content-card-header-title">
                        <div
                            // eslint-disable-next-line react/no-danger
                            dangerouslySetInnerHTML={{
                                __html: formattedOverview,
                            }}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default InsightCard;
